// Invitation acceptance. The token travels in the URL FRAGMENT (#token=…),
// which browsers never send to servers or in Referer headers, and is then
// posted in a request body (ADR-0012 §3). When the invitee must sign in
// first, the token waits in this tab's sessionStorage — it is never put in
// the login `returnTo` (that would send it to the API in a query string).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { apiUrl } from "@/api/config";
import { api, call, csrf } from "@/api/client";
import { ApiProblem } from "@/api/errors";
import { keys, useMe } from "@/api/hooks";
import { ROLE_LABELS } from "@/lib/labels";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/ui/button";
import { Callout, Details, Skeleton } from "@/ui/layout";

const PENDING_KEY = "ia-pending-invitation";
const TOKEN = /^[A-Za-z0-9_-]{43}$/;

function tokenFromLocation(): string {
  const match = /(?:^|&)token=([A-Za-z0-9_-]{43})(?:&|$)/.exec(window.location.hash.replace(/^#/, ""));
  if (match?.[1]) return match[1];
  try {
    const pending = sessionStorage.getItem(PENDING_KEY) ?? "";
    return TOKEN.test(pending) ? pending : "";
  } catch {
    return "";
  }
}

function forgetPendingToken() {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
  if (window.location.hash) history.replaceState(null, "", window.location.pathname);
}

export function InvitePage() {
  const [token] = useState(tokenFromLocation);
  const { data: me, isLoading } = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const preview = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => call(() => api.POST("/invitations/resolve", { body: { token } })),
    enabled: token !== "",
    retry: false,
  });

  if (token === "") {
    return (
      <Shell>
        <Callout tone="warning" title="Enlace incompleto">
          Este enlace de invitación no es válido. Pide a quien te invitó que te lo envíe de nuevo.
        </Callout>
      </Shell>
    );
  }
  if (!isLoading && !me) {
    const signIn = () => {
      try {
        sessionStorage.setItem(PENDING_KEY, token);
      } catch {
        // Without storage the invitee simply opens the link again after signing in.
      }
      window.location.assign(apiUrl(`/auth/login?returnTo=${encodeURIComponent("/invite")}`));
    };
    return (
      <Shell>
        <p className="text-sm text-muted">Inicia sesión o crea tu cuenta con el correo invitado para aceptar.</p>
        <Button variant="primary" onClick={signIn}>
          Continuar
        </Button>
      </Shell>
    );
  }
  if (preview.isError) {
    return (
      <Shell>
        <Callout tone="warning" title="Invitación no disponible">
          La invitación no existe, ya se usó, fue revocada o expiró.
        </Callout>
      </Shell>
    );
  }
  const accept = async () => {
    setPending(true);
    try {
      const result = await call(() => api.POST("/invitations/accept", { body: { token } }));
      csrf.set(result.csrfToken);
      forgetPendingToken(); // drop the token from the address bar and the tab storage
      await queryClient.invalidateQueries({ queryKey: keys.memberships });
      await navigate({ to: `/o/${result.organizationId}` });
    } catch (problem) {
      setError(problem instanceof ApiProblem ? problem.message : "No se pudo aceptar la invitación.");
    } finally {
      setPending(false);
    }
  };
  if (me === undefined || preview.isLoading || !preview.data) {
    return (
      <Shell>
        <Skeleton className="h-24" />
      </Shell>
    );
  }
  if (me === null) return <Navigate to="/login" />;
  return (
    <Shell>
      <Details
        items={[
          { label: "Espacio de trabajo", value: preview.data.organizationName },
          { label: "Rol", value: ROLE_LABELS[preview.data.role] },
          { label: "Para", value: preview.data.emailHint },
          { label: "Vence", value: formatDateTime(preview.data.expiresAt) },
        ]}
      />
      {error ? <Callout tone="danger">{error}</Callout> : null}
      <Button variant="primary" disabled={pending} onClick={() => void accept()}>
        Aceptar invitación
      </Button>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main id="main" className="grid min-h-dvh place-items-center bg-canvas px-4">
      <div className="grid w-full max-w-md gap-4">
        <h1 className="text-xl font-semibold">Invitación a InteractAds</h1>
        {children}
      </div>
    </main>
  );
}
